/**
 * StatusRail — slim HUD strip across the top of the Live Map.
 *
 * Phase 7A pared this down: TRACKS / ALERTS / SENSORS counts moved to
 * the RightRail tab badges (their canonical home), and the severity
 * breakdown moved to the new CriticalAlertBanner. What's left is the
 * "system alive" signal you want to glance at — never the data you
 * need to *act on*. That signal lives in three readouts:
 *
 *   STATUS PILL · clock (UTC + local) · camera lat/lng/alt
 *
 * The status pill is a single rolled-up indicator: green (all clear),
 * amber (degraded — at least one sensor offline OR a non-critical
 * alert pending), red (a CRITICAL alert is live or > half the sensor
 * fleet is offline). It's a glance gauge, not a count.
 *
 * Health roll-up still polls /sensors and /reports/summary, but it
 * collapses both into one boolean-ish state so the rail itself stays
 * quiet. Failures fall back to neutral so the bar never disappears.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { sensorsApi } from '../../services/api';
import { reportsApi } from '../../services/reportsApi';
import Readout from './Readout';
import HudTag from './HudTag';

interface Props {
  /** LIVE / REPLAY mode badge. Default LIVE. */
  mode?: 'LIVE' | 'REPLAY';
}

interface SensorRow { id: string; status: string; }

function useNow(intervalMs = 1000): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

function formatUtc(d: Date): string {
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  const ss = String(d.getUTCSeconds()).padStart(2, '0');
  return `${hh}:${mm}:${ss}Z`;
}

// Resolve the browser's timezone once on module load. Intl returns an IANA
// zone (America/New_York, Europe/London) inferred from the user's OS —
// matches "based on GPS from the browser" semantics without us doing
// reverse-geocoding ourselves. Cached because Intl.DateTimeFormat is
// expensive to instantiate per tick.
const LOCAL_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
const LOCAL_TZ_LABEL = (() => {
  // Pull a short label like "EDT" / "PST" from the formatter. Falls back
  // to "LOC" if the runtime doesn't expose the timezone abbreviation
  // (older Safari, some embedded WebViews).
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZoneName: 'short',
      timeZone: LOCAL_TZ,
    }).formatToParts(new Date());
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? 'LOC';
  } catch {
    return 'LOC';
  }
})();

const LOCAL_TIME_FORMATTER = new Intl.DateTimeFormat('en-US', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: LOCAL_TZ,
});

function formatLocal(d: Date): string {
  return LOCAL_TIME_FORMATTER.format(d);
}

type SystemHealth = 'ok' | 'degraded' | 'critical' | 'unknown';

function rollUpHealth(
  sensors: { online: number; total: number } | undefined,
  alerts: { critical: number } | undefined,
): SystemHealth {
  if (!sensors && !alerts) return 'unknown';
  if (alerts && alerts.critical > 0) return 'critical';
  if (sensors && sensors.total > 0) {
    if (sensors.online === 0) return 'critical';
    if (sensors.online < sensors.total / 2) return 'critical';
    if (sensors.online < sensors.total) return 'degraded';
  }
  return 'ok';
}

const HEALTH_COPY: Record<SystemHealth, { tone: 'success' | 'warn' | 'alert' | 'neutral'; label: string }> = {
  ok: { tone: 'success', label: 'ALL CLEAR' },
  degraded: { tone: 'warn', label: 'DEGRADED' },
  critical: { tone: 'alert', label: 'CRITICAL' },
  unknown: { tone: 'neutral', label: 'CHECKING' },
};

export default function StatusRail({ mode = 'LIVE' }: Props) {
  const now = useNow();

  const sensorsQuery = useQuery({
    queryKey: ['c2', 'sensors-rollup'],
    queryFn: async () => {
      const res = await sensorsApi.list({ pageSize: 200 });
      const list = (res?.data as SensorRow[] | undefined) ?? [];
      const online = list.filter((s) => s.status === 'ONLINE').length;
      return { online, total: list.length };
    },
    refetchInterval: 30_000,
    staleTime: 25_000,
  });

  // Critical alert count over the last 5 min — same window as the banner.
  // Cheaper than re-summarizing today's data; just need a yes/no for the pill.
  const criticalAlertsQuery = useQuery({
    queryKey: ['c2', 'critical-rollup'],
    queryFn: async () => {
      const to = new Date();
      const from = new Date(to.getTime() - 5 * 60 * 1000);
      const summary = await reportsApi.getSummary({
        from: from.toISOString(),
        to: to.toISOString(),
      });
      const critical = summary.latestAlerts.filter((a) => a.severity === 'CRITICAL').length;
      return { critical };
    },
    refetchInterval: 15_000,
    staleTime: 10_000,
  });

  const health = useMemo<SystemHealth>(
    () => rollUpHealth(sensorsQuery.data, criticalAlertsQuery.data),
    [sensorsQuery.data, criticalAlertsQuery.data],
  );
  const healthMeta = HEALTH_COPY[health];

  return (
    <div
      className={clsx(
        'flex items-center gap-5 px-4 h-7 bg-iron-900 border-b border-iron-600 select-none',
        'text-iron-200',
      )}
    >
      {/* Mode + health — leftmost */}
      <div className="flex items-center gap-2.5">
        <HudTag tone={mode === 'LIVE' ? 'success' : 'sky'}>
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-current animate-pulse-dot" />
          {mode}
        </HudTag>
        <HudTag tone={healthMeta.tone}>{healthMeta.label}</HudTag>
      </div>

      <div className="h-3.5 w-px bg-iron-600" />

      {/* Clock — UTC + local (timezone auto-detected from the browser).
          Camera readouts (lat/lng/alt) used to live here too; removed in
          Phase 8 since they competed with the map's own coordinate display
          and operators rarely scanned numbers off the top bar. */}
      <Readout label="UTC" value={formatUtc(now)} inline />
      <Readout label={LOCAL_TZ_LABEL} value={formatLocal(now)} inline />

      {/* Right-aligned wordmark — keeps the bar feeling like a header. */}
      <div className="ml-auto flex items-center gap-2 font-mono text-[10px] text-iron-400 tracking-wider">
        ASNE C2
      </div>
    </div>
  );
}
